import { DarkTiles } from "@/components/map/DarkTiles";
import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { MapPin, RefreshCw, Store, Bike, Navigation, Users, Eye } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Custom icons
const partnerIcon = L.divIcon({
  className: 'custom-partner-icon',
  html: `<div style="background: #2563eb; width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 10px rgba(0,0,0,0.3);">
    <span style="font-size: 18px;">🏪</span>
  </div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 18],
  popupAnchor: [0, -18]
});

const courierActiveIcon = L.divIcon({
  className: 'custom-courier-active-icon',
  html: `<div style="background: #16a34a; width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 10px rgba(0,0,0,0.3); animation: pulse 2s infinite;">
    <span style="font-size: 20px;">🏍️</span>
  </div>`,
  iconSize: [40, 40],
  iconAnchor: [20, 20],
  popupAnchor: [0, -20]
});

const courierInactiveIcon = L.divIcon({
  className: 'custom-courier-inactive-icon',
  html: `<div style="background: #9ca3af; width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 10px rgba(0,0,0,0.3);">
    <span style="font-size: 18px;">🏍️</span>
  </div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 18],
  popupAnchor: [0, -18]
});

// Component to fit map bounds
function FitBounds({ positions }) {
  const map = useMap();

  useEffect(() => {
    if (positions && positions.length > 0) {
      const bounds = L.latLngBounds(positions);
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [positions, map]);

  return null;
}

export default function AdminCarte() {
  const [partners, setPartners] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [activeCouriers, setActiveCouriers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState("all"); // "all", "partners", "couriers"
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);

  // Dakar center
  const dakarCenter = [14.6928, -17.4467];

  const fetchData = useCallback(async () => {
    try {
      const [partnersRes, couriersRes, activeRes] = await Promise.all([
        axios.get(`${API}/partners`),
        axios.get(`${API}/couriers`),
        axios.get(`${API}/gps/active-couriers`)
      ]);

      // Filter partners with location
      const partnersWithLocation = partnersRes.data.filter(
        p => p.latitude && p.longitude
      );
      setPartners(partnersWithLocation);

      // Set all couriers
      setCouriers(couriersRes.data);

      // Set active couriers with GPS
      setActiveCouriers(activeRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();

    // Auto-refresh every 15 seconds for live tracking
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchData, 15000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [fetchData, autoRefresh]);

  // Get all positions for map bounds
  const getAllPositions = () => {
    const positions = [];

    if (viewMode === "all" || viewMode === "partners") {
      partners.forEach(p => {
        if (p.latitude && p.longitude) {
          positions.push([p.latitude, p.longitude]);
        }
      });
    }

    if (viewMode === "all" || viewMode === "couriers") {
      activeCouriers.forEach(c => {
        if (c.latitude && c.longitude) {
          positions.push([c.latitude, c.longitude]);
        }
      });
    }

    return positions;
  };

  const handleRefresh = () => {
    setIsLoading(true);
    fetchData();
    toast.success("Données actualisées");
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Store className="w-8 h-8" />
              <div>
                <p className="text-sm opacity-90">Partenaires</p>
                <p className="text-2xl font-bold">{partners.length}</p>
                <p className="text-xs opacity-75">avec géolocalisation</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Bike className="w-8 h-8" />
              <div>
                <p className="text-sm opacity-90">Livreurs actifs</p>
                <p className="text-2xl font-bold">{activeCouriers.length}</p>
                <p className="text-xs opacity-75">en temps réel</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white border-0">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8" />
              <div>
                <p className="text-sm opacity-90">Total livreurs</p>
                <p className="text-2xl font-bold">{couriers.length}</p>
                <p className="text-xs opacity-75">inscrits</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Navigation className="w-8 h-8" />
              <div>
                <p className="text-sm opacity-90">Zone couverte</p>
                <p className="text-2xl font-bold">Dakar</p>
                <p className="text-xs opacity-75">et environs</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Controls */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600">Afficher:</span>
              <div className="flex rounded-full bg-gray-100 p-1">
                <Button
                  size="sm"
                  variant={viewMode === "all" ? "default" : "ghost"}
                  className="rounded-full"
                  onClick={() => setViewMode("all")}
                >
                  Tout
                </Button>
                <Button
                  size="sm"
                  variant={viewMode === "partners" ? "default" : "ghost"}
                  className="rounded-full"
                  onClick={() => setViewMode("partners")}
                >
                  <Store className="w-4 h-4 mr-1" /> Partenaires
                </Button>
                <Button
                  size="sm"
                  variant={viewMode === "couriers" ? "default" : "ghost"}
                  className="rounded-full"
                  onClick={() => setViewMode("couriers")}
                >
                  <Bike className="w-4 h-4 mr-1" /> Livreurs
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm">Auto-actualisation</span>
              </label>

              <Button
                onClick={handleRefresh}
                variant="outline"
                size="sm"
                className="rounded-full"
                disabled={isLoading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Actualiser
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Map */}
      <Card className="overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-slate-800 to-slate-700 text-white">
          <CardTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            Carte en temps réel
            {autoRefresh && (
              <span className="ml-2 flex items-center gap-1 text-xs font-normal bg-green-500 px-2 py-1 rounded-full">
                <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                Live
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div style={{ height: '500px' }}>
            <MapContainer
              center={dakarCenter}
              zoom={12}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={true}
            >
              <DarkTiles />

              <FitBounds positions={getAllPositions()} />

              {/* Partner markers */}
              {(viewMode === "all" || viewMode === "partners") && partners.map((partner) => (
                <Marker
                  key={`partner-${partner.id}`}
                  position={[partner.latitude, partner.longitude]}
                  icon={partnerIcon}
                >
                  <Popup>
                    <div className="text-center min-w-[200px]">
                      <div className="bg-blue-100 -mx-3 -mt-2 px-3 py-2 mb-3">
                        <strong className="text-blue-800">🏪 {partner.business_name || partner.full_name}</strong>
                      </div>
                      <div className="space-y-1 text-sm">
                        <p><strong>Type:</strong> {partner.business_type || 'Commerce'}</p>
                        <p><strong>Contact:</strong> {partner.phone || partner.email}</p>
                        {partner.address && <p><strong>Adresse:</strong> {partner.address}</p>}
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))}

              {/* Active courier markers */}
              {(viewMode === "all" || viewMode === "couriers") && activeCouriers.map((courier) => (
                <Marker
                  key={`courier-${courier.courier_id}`}
                  position={[courier.latitude, courier.longitude]}
                  icon={courierActiveIcon}
                >
                  <Popup>
                    <div className="text-center min-w-[200px]">
                      <div className="bg-green-100 -mx-3 -mt-2 px-3 py-2 mb-3">
                        <strong className="text-green-800">🏍️ {courier.courier_name}</strong>
                        <span className="ml-2 inline-flex items-center gap-1 text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">
                          <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
                          Actif
                        </span>
                      </div>
                      <div className="space-y-1 text-sm">
                        {courier.speed && (
                          <p><strong>Vitesse:</strong> {Math.round(courier.speed * 3.6)} km/h</p>
                        )}
                        <p className="text-xs text-gray-500">
                          Dernière mise à jour: {new Date(courier.updated_at).toLocaleTimeString('fr-FR')}
                        </p>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center justify-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm">🏪</div>
              <span className="text-sm">Partenaire / Boutique</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white text-sm animate-pulse">🏍️</div>
              <span className="text-sm">Livreur actif (temps réel)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-gray-400 rounded-full flex items-center justify-center text-white text-sm">🏍️</div>
              <span className="text-sm">Livreur hors ligne</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Active Couriers List */}
      {activeCouriers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bike className="w-5 h-5" />
              Livreurs actifs en ce moment ({activeCouriers.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {activeCouriers.map((courier) => (
                <div
                  key={courier.courier_id}
                  className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg"
                >
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white">
                    🏍️
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{courier.courier_name}</p>
                    <p className="text-xs text-gray-500">
                      {courier.speed ? `${Math.round(courier.speed * 3.6)} km/h • ` : ''}
                      {new Date(courier.updated_at).toLocaleTimeString('fr-FR')}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-green-600">
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                    En ligne
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* CSS for animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
      `}</style>
    </div>
  );
}
